import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getAccessToken } from '../../../../../../lib/session';
import { resolveApiRoot } from '../../../../../../lib/api-fetcher';

export interface AuctionSocketState {
  auction: Record<string, unknown>;
  bids: Record<string, unknown>[];
}

/**
 * Opens a real socket.io connection to the API's `/auctions` namespace and
 * joins the room for `auctionId`, receiving `auction:update` pushes emitted
 * by the server whenever the auction's domain events fire (opened, bid
 * placed, bidding closed, confirmed). `connected` reflects the real socket
 * state — it is false until the handshake succeeds and flips back to false
 * on disconnect or connect error.
 */
export function useAuctionWebSocket(auctionId: string | null) {
  const [connected, setConnected] = useState(false);
  const [auctionState, setAuctionState] = useState<AuctionSocketState | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!auctionId) {
      setConnected(false);
      setAuctionState(null);
      return;
    }

    const token = getAccessToken();
    const apiRoot = resolveApiRoot();
    const socket = io(`${apiRoot}/auctions`, {
      extraHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
      withCredentials: true,
    });
    socketRef.current = socket;

    const handleConnect = () => {
      setConnected(true);
      socket.emit('auction:join', { auctionId });
    };
    const handleDisconnect = () => setConnected(false);
    const handleConnectError = () => setConnected(false);
    const handleUpdate = (payload: AuctionSocketState) => setAuctionState(payload);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('auction:update', handleUpdate);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('auction:update', handleUpdate);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setAuctionState(null);
    };
  }, [auctionId]);

  return {
    connected,
    auctionState,
  };
}
